import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle, XCircle, Plus, Minus, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
const faqs = [{
  q: 'What is a wall mounted air conditioning unit?',
  a: 'It is a versatile indoor unit mounted high on a wall, connected to an outdoor condenser. It draws in warm air, cools or heats it, and blows it back into the room.'
}, {
  q: 'Are they suitable for commercial use?',
  a: 'Absolutely. While common in homes, commercial-grade wall units are robust, highly efficient, and perfect for server rooms, meeting rooms, and individual offices.'
}, {
  q: 'How long does installation take?',
  a: 'Wall mounted systems are typically the fastest to install. A standard single-split system can often be installed and commissioned within a single day.'
}, {
  q: 'Can they heat as well as cool?',
  a: 'Yes, all modern commercial wall mounted units are heat pumps, providing energy-efficient heating in winter and cooling in summer.'
}, {
  q: 'How much do they cost to run?',
  a: "Thanks to inverter technology, they are incredibly efficient. They adjust their power output to match the room's requirements, keeping running costs low."
}];
export function WallMounted() {
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-2440">
      {/* 1. Hero Section */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-2441">
        <div className="container mx-auto px-6" data-id="element-2442">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-2443">
            <div className="lg:col-span-7" data-id="element-2444">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-2445">
                Systems / Wall Mounted
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-2446">
                Wall mounted air conditioning
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed mb-10" data-id="element-2447">
                Practical, cost-effective, and highly efficient commercial
                cooling and heating for individual rooms and targeted spaces.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-2448">
                <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2449">
                  Book a site survey
                </Link>
                <Link to="/contact" className="bg-white hover:bg-primary-dark/5 text-primary-dark border border-primary-dark/10 px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2450">
                  Speak to our team
                </Link>
              </motion.div>
            </div>
            <div className="lg:col-span-5" data-id="element-2451">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6
            }} className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg" data-id="element-2452">
                <img src="https://cdn.magicpatterns.com/uploads/akgyqHU1pPLUBdVziFo8Cy/wall-mounted-air-conditioning.webp" alt="Wall mounted air conditioning unit" className="w-full h-full object-cover" data-id="element-2453" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Best For */}
      <section className="bg-white py-16 border-b border-primary-dark/5" data-id="element-2454">
        <div className="container mx-auto px-6" data-id="element-2455">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2456">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary-dark/40 mb-6" data-id="element-2457">
              Best for
            </h2>
            <p className="text-xl md:text-2xl text-primary-dark/80 leading-relaxed font-medium" data-id="element-2458">
              Server rooms, meeting rooms, individual offices, and areas
              requiring supplementary cooling or heating.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Key Benefits */}
      <section className="bg-[#E8F0F8]/50 py-24" data-id="element-2459">
        <div className="container mx-auto px-6" data-id="element-2460">
          <div className="max-w-4xl mx-auto" data-id="element-2461">
            <h2 className="text-3xl md:text-4xl font-serif mb-10 text-center" data-id="element-2462">
              Key benefits
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-2463">
              {['Fast and straightforward installation', 'Individual room temperature control', 'Provides both heating and cooling', 'Compact footprint saves floor space', 'Highly cost-effective solution', 'Easy access for maintenance and filter cleaning'].map((benefit, i) => <div key={i} className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm" data-id="element-2464">
                  <CheckCircle className="w-6 h-6 text-accent shrink-0" data-id="element-2465" />
                  <span className="font-medium text-primary-dark/80" data-id="element-2466">
                    {benefit}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Is this the right fit? */}
      <section className="bg-white py-24" data-id="element-2467">
        <div className="container mx-auto px-6" data-id="element-2468">
          <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2469">
            Is this the right fit?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-2470">
            <div className="bg-[#E8F0F8] p-10 rounded-3xl border border-primary-dark/5" data-id="element-2471">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-success" data-id="element-2472">
                <CheckCircle className="w-7 h-7" data-id="element-2473" />
                Good fit for
              </h3>
              <ul className="space-y-4" data-id="element-2474">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2475">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2476" />
                  <span data-id="element-2477">Individual rooms and enclosed offices</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2478">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2479" />
                  <span data-id="element-2480">Server rooms requiring dedicated cooling</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2481">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2482" />
                  <span data-id="element-2483">Supplementary cooling in specific hot-spots</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2484">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2485" />
                  <span data-id="element-2486">
                    Projects with tighter budgets or rapid installation needs
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-10 rounded-3xl border border-primary-dark/10 shadow-sm" data-id="element-2487">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-error" data-id="element-2488">
                <XCircle className="w-7 h-7" data-id="element-2489" />
                May not be ideal for
              </h3>
              <ul className="space-y-4" data-id="element-2490">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2491">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2492" />
                  <span data-id="element-2493">
                    Large open-plan spaces requiring even distribution
                  </span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2494">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2495" />
                  <span data-id="element-2496">
                    Environments where aesthetics demand completely hidden units
                  </span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2497">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2498" />
                  <span data-id="element-2499">
                    Rooms lacking suitable external wall access for pipework
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. AccuTemp design approach */}
      <section className="bg-primary-dark text-white py-24" data-id="element-2500">
        <div className="container mx-auto px-6" data-id="element-2501">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2502">
            <h2 className="text-3xl md:text-4xl font-serif mb-8" data-id="element-2503">
              The AccuTemp design approach
            </h2>
            <p className="text-lg text-white/80 leading-relaxed" data-id="element-2504">
              We don't just install systems; we engineer solutions. Our in-house
              commercial team helps specify the exact wall mounted setup you
              need based on your room layout, comfort requirements, airflow
              dynamics, heat load, usage patterns, and budget. This design-led
              expertise ensures your system operates efficiently, quietly, and
              reliably for years to come.
            </p>
          </div>
        </div>
      </section>

      {/* 6. FAQs */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-2505">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-2506">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2507">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-2508">
            {faqs.map((faq, i) => <div key={i} className="border border-primary-dark/10 rounded-2xl overflow-hidden bg-white" data-id="element-2509">
                <button onClick={() => setOpenFaqId(openFaqId === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-primary-light/30 transition-colors" data-id="element-2510">
                  <span className="font-medium text-primary-dark pr-8" data-id="element-2511">
                    {faq.q}
                  </span>
                  <span className="flex-shrink-0 text-primary-dark/40" data-id="element-2512">
                    {openFaqId === i ? <Minus className="w-5 h-5" data-id="element-2513" /> : <Plus className="w-5 h-5" data-id="element-2514" />}
                  </span>
                </button>
                <AnimatePresence data-id="element-2515">
                  {openFaqId === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-2516">
                      <div className="px-6 pb-6 pt-2 text-primary-dark/70 leading-relaxed border-t border-primary-dark/5" data-id="element-2517">
                        <p data-id="element-2518">{faq.a}</p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Brochure / literature section */}
      <section className="bg-white py-24 border-b border-primary-dark/5" data-id="element-2519">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-2520">
          <h2 className="text-2xl font-serif mb-8" data-id="element-2521">Technical literature</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-2522">
            <a href="https://accutemp.co.uk/products/Large%20Wall%20Mounted%20FAA-B%20RZAG.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2523">
              <FileText className="w-5 h-5 text-accent" data-id="element-2524" />
              Download brochure
            </a>
            <a href="https://accutemp.co.uk/products/Commerical%20Cat.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2525">
              <Download className="w-5 h-5 text-accent" data-id="element-2526" />
              View commercial catalogue
            </a>
          </div>
        </div>
      </section>

      {/* 8. Bottom CTA */}
      <section className="bg-[#D4DDE8] py-24 text-center" data-id="element-2527">
        <div className="container mx-auto px-6" data-id="element-2528">
          <div className="max-w-2xl mx-auto" data-id="element-2529">
            <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-10" data-id="element-2530">
              Ready to explore wall mounted options?
            </h2>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-2531">
              <Link to="/contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2532">
                Book a site survey
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2533" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-3 bg-white hover:bg-white/80 text-primary-dark px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2534">
                Get expert advice
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>;
}